'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { CSSVars } from '@/styles/styles.config';
import { HiOutlineCog } from '@react-icons/all-files/hi/HiOutlineCog';
import { HiOutlineSun } from '@react-icons/all-files/hi/HiOutlineSun';
import { HiOutlineSparkles } from '@react-icons/all-files/hi/HiOutlineSparkles';
import { HiOutlineLightningBolt } from '@react-icons/all-files/hi/HiOutlineLightningBolt';
import { HiOutlineHome } from '@react-icons/all-files/hi/HiOutlineHome';
import { HiOutlineShieldCheck } from '@react-icons/all-files/hi/HiOutlineShieldCheck';
import { HiOutlineKey } from '@react-icons/all-files/hi/HiOutlineKey';
import { HiOutlineOfficeBuilding } from '@react-icons/all-files/hi/HiOutlineOfficeBuilding';
import { HiOutlineGlobe } from '@react-icons/all-files/hi/HiOutlineGlobe';
import { HiOutlineBriefcase } from '@react-icons/all-files/hi/HiOutlineBriefcase';
import { HiOutlineLocationMarker } from '@react-icons/all-files/hi/HiOutlineLocationMarker';
import { HiOutlineScale } from '@react-icons/all-files/hi/HiOutlineScale';
import { HiOutlineUsers } from '@react-icons/all-files/hi/HiOutlineUsers';
import { HiOutlineHeart } from '@react-icons/all-files/hi/HiOutlineHeart';
import { HiOutlineFire } from '@react-icons/all-files/hi/HiOutlineFire';
import { HiOutlineBookOpen } from '@react-icons/all-files/hi/HiOutlineBookOpen';
import { HiOutlineCash } from '@react-icons/all-files/hi/HiOutlineCash';
import { HiOutlineDesktopComputer } from '@react-icons/all-files/hi/HiOutlineDesktopComputer';
import { HiOutlineTag } from '@react-icons/all-files/hi/HiOutlineTag';
import { HiOutlineTerminal } from '@react-icons/all-files/hi/HiOutlineTerminal';
import { HiOutlineTemplate } from '@react-icons/all-files/hi/HiOutlineTemplate';
import { HiOutlineSupport } from '@react-icons/all-files/hi/HiOutlineSupport';
import { HiOutlineLibrary } from '@react-icons/all-files/hi/HiOutlineLibrary';
import { HiOutlineChartBar } from '@react-icons/all-files/hi/HiOutlineChartBar';
import { HiOutlineClipboardCheck } from '@react-icons/all-files/hi/HiOutlineClipboardCheck';
import { HiOutlineDocumentReport } from '@react-icons/all-files/hi/HiOutlineDocumentReport';
import { HiOutlinePuzzle } from '@react-icons/all-files/hi/HiOutlinePuzzle';
import { HiOutlineUserGroup } from '@react-icons/all-files/hi/HiOutlineUserGroup';
import { HiOutlineCollection } from '@react-icons/all-files/hi/HiOutlineCollection';
import { HiOutlineBadgeCheck } from '@react-icons/all-files/hi/HiOutlineBadgeCheck';
import {
  ISubVertical,
  ITestimonial,
  IHowItWorksStep,
  ICtaBand,
} from './verticalData';

const SUB_VERTICAL_ICONS: Record<string, React.ReactNode> = {
  /* Home Services */
  'Plumbing & HVAC': <HiOutlineCog />,
  'Landscaping & Lawn Care': <HiOutlineSun />,
  'Cleaning Services': <HiOutlineSparkles />,
  'Electrical Services': <HiOutlineLightningBolt />,
  'Roofing & Renovation': <HiOutlineHome />,
  'Pest Control': <HiOutlineShieldCheck />,
  /* Real Estate */
  'Residential Agents': <HiOutlineHome />,
  'Mortgage Lenders': <HiOutlineOfficeBuilding />,
  'Property Portals': <HiOutlineGlobe />,
  'Commercial Real Estate': <HiOutlineBriefcase />,
  'Property Developers': <HiOutlineLocationMarker />,
  'Property Management': <HiOutlineKey />,
  /* Legal */
  'Personal Injury': <HiOutlineScale />,
  'Family Law': <HiOutlineUsers />,
  'Immigration': <HiOutlineGlobe />,
  'Criminal Defence': <HiOutlineShieldCheck />,
  'Estate Planning': <HiOutlineLibrary />,
  'Employment Law': <HiOutlineBriefcase />,
  /* Financial Services */
  'Financial Advisors': <HiOutlineChartBar />,
  'Accountants & Tax': <HiOutlineClipboardCheck />,
  'Wealth Management': <HiOutlineCash />,
  'Investment Firms': <HiOutlineOfficeBuilding />,
  'Fintech Startups': <HiOutlineLightningBolt />,
  'Insurance Brokers': <HiOutlineShieldCheck />,
  /* Insurance */
  'Life Insurance': <HiOutlineHeart />,
  'Auto Insurance': <HiOutlineShieldCheck />,
  'Home Insurance': <HiOutlineHome />,
  'Health Insurance': <HiOutlineSupport />,
  'Business Insurance': <HiOutlineBriefcase />,
  'Specialty Insurance': <HiOutlinePuzzle />,
  /* Healthcare */
  'Dental Clinics': <HiOutlineSparkles />,
  'Private Medical Practices': <HiOutlineHeart />,
  'Telehealth': <HiOutlineDesktopComputer />,
  'Aesthetics & Med Spas': <HiOutlineSun />,
  'Mental Health': <HiOutlineSupport />,
  'Chiropractic & Physical Therapy': <HiOutlineUsers />,
  /* Fitness */
  'Personal Trainers': <HiOutlineFire />,
  'Gyms & Studios': <HiOutlineOfficeBuilding />,
  'Nutrition Coaches': <HiOutlineHeart />,
  'Yoga & Pilates': <HiOutlineSun />,
  'Online Fitness Programs': <HiOutlineDesktopComputer />,
  'Wellness Apps': <HiOutlineTemplate />,
  /* SaaS */
  'B2B SaaS Products': <HiOutlineDesktopComputer />,
  'Marketing Agencies': <HiOutlineChartBar />,
  'IT Service Providers': <HiOutlineCog />,
  'DevTools': <HiOutlineTerminal />,
  'Consulting Firms': <HiOutlineBriefcase />,
  'Managed Service Providers': <HiOutlineSupport />,
  /* E-Commerce */
  'DTC Brands': <HiOutlineTag />,
  'Subscription Boxes': <HiOutlineCollection />,
  'Beauty & Skincare': <HiOutlineSparkles />,
  'Fashion & Apparel': <HiOutlineBadgeCheck />,
  'Home & Garden': <HiOutlineHome />,
  'Food & Beverage': <HiOutlineFire />,
  /* Education */
  'Online Course Creators': <HiOutlineDesktopComputer />,
  'Bootcamps': <HiOutlineTerminal />,
  'Professional Training': <HiOutlineDocumentReport />,
  'Universities': <HiOutlineLibrary />,
  'Tutoring Services': <HiOutlineUserGroup />,
  'Corporate Learning': <HiOutlineOfficeBuilding />,
};
import {
  StyledSection,
  StyledInner,
  StyledEyebrow,
  StyledSectionTitle,
  StyledSectionSub,
  StyledSubVerticalsGrid,
  StyledSubVerticalCard,
  StyledWidgetTags,
  StyledWidgetTag,
  StyledTestimonialGrid,
  StyledTestimonialCard,
  StyledStepsRow,
  StyledStep,
  StyledStepsCta,
  StyledPlatformCarousel,
  StyledPlatformCta,
  StyledCtaBand,
  StyledCtaBandInner,
} from './VerticalHubContent.styles';

const PLATFORM_LOGOS = [
  { name: 'Shopify', slug: 'shopify', file: 'Shopify.png', width: 140 },
  { name: 'WordPress', slug: 'wordpress', file: 'Wordpress.png', width: 176 },
  { name: 'Wix', slug: 'wix', file: 'Wix.png', width: 102 },
  { name: 'Webflow', slug: 'webflow', file: 'Webflow.png', width: 160 },
  { name: 'Squarespace', slug: 'squarespace', file: 'Squarespace.png', width: 216 },
  { name: 'BigCommerce', slug: 'bigcommerce', file: 'Bigcommerce.png', width: 176 },
  { name: 'Weebly', slug: 'weebly', file: 'Weebly.png', width: 116 },
  { name: 'Joomla', slug: 'joomla', file: 'Joomla.png', width: 196 },
  { name: 'Duda', slug: 'duda', file: 'Duda.png', width: 94 },
  { name: 'Magento', slug: 'magento', file: 'Magento.png', width: 144 },
  { name: 'Elementor', slug: 'elementor', file: 'Elementor.png', width: 160 },
  { name: 'Unbounce', slug: 'unbounce', file: 'Unbounce.png', width: 174 },
];

interface IProps {
  verticalName: string;
  subVerticals: ISubVertical[];
  testimonials?: ITestimonial[];
  howItWorks: IHowItWorksStep[];
  ctaBand: ICtaBand;
}

export const VerticalHubContent = ({
  verticalName,
  subVerticals,
  testimonials,
  howItWorks,
  ctaBand,
}: IProps) => {
  return (
    <>
      {/* ─── SUB-VERTICALS ─── */}
      <StyledSection>
        <StyledInner>
          <div style={{ textAlign: 'center' }}>
            <StyledEyebrow>Your Industry</StyledEyebrow>
            <StyledSectionTitle>
              Built for Every {verticalName.charAt(0).toUpperCase() + verticalName.slice(1)} Niche
            </StyledSectionTitle>
            <StyledSectionSub $center>
              Whether you&apos;re a one-person operation or a regional franchise,
              Common Ninja has the right widgets for your trade.
            </StyledSectionSub>
          </div>
          <StyledSubVerticalsGrid>
            {subVerticals.map((sv) => (
              <StyledSubVerticalCard key={sv.name}>
                <div className="sv-icon">{SUB_VERTICAL_ICONS[sv.name] || sv.icon}</div>
                <div className="sv-name">{sv.name}</div>
                {sv.description && <div className="sv-desc">{sv.description}</div>}
                <StyledWidgetTags>
                  {sv.relevantWidgets.map((w) => (
                    <StyledWidgetTag key={w}>{w}</StyledWidgetTag>
                  ))}
                </StyledWidgetTags>
              </StyledSubVerticalCard>
            ))}
          </StyledSubVerticalsGrid>
        </StyledInner>
      </StyledSection>

      {/* ─── TESTIMONIALS ─── */}
      {testimonials && testimonials.length > 0 && (
        <StyledSection $bg={CSSVars.gray50}>
          <StyledInner>
            <div style={{ textAlign: 'center' }}>
              <StyledEyebrow>What Businesses Say</StyledEyebrow>
              <StyledSectionTitle>
                Real Results from Real {verticalName.charAt(0).toUpperCase() + verticalName.slice(1)} Businesses
              </StyledSectionTitle>
            </div>
            <StyledTestimonialGrid>
              {testimonials.map((t, i) => (
                <StyledTestimonialCard key={i}>
                  <span className="quote-mark">&ldquo;</span>
                  <p className="quote-text">{t.quote}</p>
                  {t.rating && (
                    <div className="quote-stars">
                      {Array.from({ length: t.rating }).map((_, j) => (
                        <span key={j}>&#9733;</span>
                      ))}
                    </div>
                  )}
                  <div className="quote-author">
                    <span className="author-name">{t.authorName}</span>
                    {t.authorRole && (
                      <span className="author-role">{t.authorRole}</span>
                    )}
                  </div>
                </StyledTestimonialCard>
              ))}
            </StyledTestimonialGrid>
          </StyledInner>
        </StyledSection>
      )}

      {/* ─── HOW IT WORKS ─── */}
      <StyledSection $bg={testimonials?.length ? CSSVars.white : CSSVars.gray50}>
        <StyledInner>
          <div style={{ textAlign: 'center' }}>
            <StyledEyebrow>Getting Started</StyledEyebrow>
            <StyledSectionTitle>
              Up and Running in {howItWorks.length} Steps — No Developer Needed
            </StyledSectionTitle>
          </div>
          <StyledStepsRow>
            {howItWorks.map((step) => (
              <StyledStep key={step.number}>
                <div className="step-num">{step.number}</div>
                <div className="step-title">{step.title}</div>
                <p className="step-desc">{step.description}</p>
              </StyledStep>
            ))}
          </StyledStepsRow>
          <StyledStepsCta>
            <Link href="/signup">Start Building for Free &rarr;</Link>
          </StyledStepsCta>
        </StyledInner>
      </StyledSection>

      {/* ─── PLATFORM SECTION ─── */}
      <StyledSection $bg={CSSVars.gray50}>
        <StyledInner>
          <div style={{ textAlign: 'center' }}>
            <StyledEyebrow>Works Everywhere</StyledEyebrow>
            <StyledSectionTitle>
              One Embed Code. Every Platform.
            </StyledSectionTitle>
            <StyledSectionSub $center>
              Common Ninja widgets work out-of-the-box with every major website
              builder and e-commerce platform. No plugins, no special setup — just
              paste a single line of code and you&apos;re live.
            </StyledSectionSub>
          </div>
          <StyledPlatformCarousel>
            <div className="carousel-track">
              {[...PLATFORM_LOGOS, ...PLATFORM_LOGOS].map((p, i) => (
                <Link key={`${p.slug}-${i}`} href={`/platforms/${p.slug}`} className="logo-item">
                  <Image src={`/assets/platforms/${p.file}`} alt={`${p.name} logo`} width={p.width} height={36} />
                </Link>
              ))}
            </div>
          </StyledPlatformCarousel>
          <StyledPlatformCta>
            <Link href="/platforms">See All 200+ Supported Platforms &rarr;</Link>
          </StyledPlatformCta>
        </StyledInner>
      </StyledSection>

      {/* ─── CTA BAND ─── */}
      <StyledCtaBand>
        <StyledCtaBandInner>
          <div className="cta-badge">
            &#128640; Join 500,000+ websites already using Common Ninja
          </div>
          <h2>{ctaBand.heading}</h2>
          <p className="cta-sub">{ctaBand.subtext}</p>
          <div className="cta-buttons">
            <Link href={ctaBand.primaryCta.href} className="cta-primary">
              {ctaBand.primaryCta.text}
            </Link>
            {ctaBand.secondaryCta && (
              <Link href={ctaBand.secondaryCta.href} className="cta-secondary">
                {ctaBand.secondaryCta.text}
              </Link>
            )}
          </div>
          <p className="cta-note">
            Free plan available forever &middot; Setup in minutes &middot; Works with any website platform
          </p>
        </StyledCtaBandInner>
      </StyledCtaBand>
    </>
  );
};
